import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';

export default class EisaScoringRaceRoute extends Route
{
	@service ajax

	async model(params)
	{
		const config = await this.fetchConfig();
		const results = await this.fetchAllResults(config);
		const raceIndex = parseInt(params.race_index);
		return { config, results, raceIndex };
	}

	getEmptyConfig()
	{
		return { teams: [], nordic: { races: [] }, alpine: { races: [] } };
	}

	async fetchConfig()
	{
		try
		{
			return await this.ajax.request("eisaScoringConfig.json");
		}
		catch (error)
		{
			console.warn('Failed to load EISA scoring config:', error);
			return this.getEmptyConfig();
		}
	}

	async fetchAllResults(config)
	{
		// Collect unique results files from nordic races (JSON format)
		const nordicFiles = new Set();
		for (const race of config?.nordic?.races || [])
		{
			if (race.resultsFile)
			{
				nordicFiles.add(race.resultsFile);
			}
		}

		// Collect unique results files from alpine races (FIS XML format)
		const alpineFiles = new Set();
		for (const race of config?.alpine?.races || [])
		{
			if (race.resultsFile)
			{
				alpineFiles.add(race.resultsFile);
			}
		}

		const results = {};

		// Fetch nordic JSON files
		for (const fileName of nordicFiles)
		{
			try
			{
				let data = await this.ajax.request(fileName);
				data = data.map(r => r.data.attributes);
				results[fileName] = data;
			}
			catch (error)
			{
				console.warn(`Failed to load nordic results file: ${fileName}`, error);
				results[fileName] = [];
			}
		}

		// Fetch and parse alpine XML files
		for (const fileName of alpineFiles)
		{
			try
			{
				const xmlText = await this.ajax.requestXML(fileName);
				results[fileName] = this.parseFisXml(xmlText);
			}
			catch (error)
			{
				console.warn(`Failed to load alpine results file: ${fileName}`, error);
				results[fileName] = [];
			}
		}

		return results;
	}

	parseFisXml(xmlText)
	{
		const parser = new DOMParser();
		const doc = parser.parseFromString(xmlText, 'text/xml');
		const results = [];

		// Only get AL_ranked elements (finishers with status="QLF")
		const rankedElements = doc.querySelectorAll('AL_ranked');

		for (const ranked of rankedElements)
		{
			const status = ranked.getAttribute('Status');
			if (status !== 'QLF') continue;

			const rank = ranked.querySelector('Rank')?.textContent;
			const competitor = ranked.querySelector('Competitor');
			const alResult = ranked.querySelector('AL_result');

			if (!competitor || !alResult) continue;

			const totalTime = alResult.querySelector('Totaltime')?.textContent;
			if (!totalTime) continue;

			const lastName = competitor.querySelector('Lastname')?.textContent || '';
			const firstName = competitor.querySelector('Firstname')?.textContent || '';
			const club = competitor.querySelector('Clubname')?.textContent || '';

			results.push({
				place: parseInt(rank, 10),
				firstName: firstName,
				lastName: lastName,
				club: club,
				totalTime: totalTime,
				status: 'OK'
			});
		}

		return results;
	}

	setupController(controller, model)
	{
		controller.set('event', this.modelFor('event'));
		controller.set('config', model.config);
		controller.set('results', model.results);
		controller.set('raceIndex', model.raceIndex);
	}
}
